/**
 * Marketing consent (advertising channel scope).
 * Single source of truth: localStorage['truwe_marketing_consent'].
 * Values: 'granted' | 'denied' | 'unknown'. Default = 'unknown'.
 *
 * Consent is NEVER inferred from signup, checkout or purchase.
 * The banner or an admin toggle is the only writer.
 */

export type ConsentStatus = "granted" | "denied" | "unknown";

const KEY = "truwe_marketing_consent";
const META_KEY = "truwe_marketing_consent_meta";
export const CONSENT_POLICY_VERSION = "2026-07-10";
export const CONSENT_CHANNEL = "advertising";
export const CONSENT_EVENT = "truwe:marketing-consent-change";

export interface ConsentRecord {
  status: ConsentStatus;
  source: string;
  timestamp: string;
  policyVersion: string;
  channel: string;
}

function readMeta(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(META_KEY);
    return raw ? (JSON.parse(raw) as ConsentRecord) : null;
  } catch {
    return null;
  }
}

export function getConsent(): ConsentStatus {
  if (typeof window === "undefined") return "unknown";
  try {
    const v = window.localStorage.getItem(KEY);
    if (v === "granted" || v === "denied") return v;
  } catch {
    /* ignore */
  }
  return "unknown";
}

export function getConsentRecord(): ConsentRecord {
  const status = getConsent();
  const meta = readMeta();
  return (
    meta ?? {
      status,
      source: "default",
      timestamp: new Date(0).toISOString(),
      policyVersion: CONSENT_POLICY_VERSION,
      channel: CONSENT_CHANNEL,
    }
  );
}

export function setConsent(status: ConsentStatus, source: string): ConsentRecord {
  const record: ConsentRecord = {
    status,
    source,
    timestamp: new Date().toISOString(),
    policyVersion: CONSENT_POLICY_VERSION,
    channel: CONSENT_CHANNEL,
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(KEY, status);
      window.localStorage.setItem(META_KEY, JSON.stringify(record));
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: record }));
    } catch {
      /* ignore */
    }
  }
  return record;
}

export function onConsentChange(cb: (r: ConsentRecord) => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => {
    const detail = (e as CustomEvent<ConsentRecord>).detail ?? getConsentRecord();
    cb(detail);
  };
  const storageHandler = (e: StorageEvent) => {
    if (e.key === KEY || e.key === META_KEY) cb(getConsentRecord());
  };
  window.addEventListener(CONSENT_EVENT, handler);
  window.addEventListener("storage", storageHandler);
  return () => {
    window.removeEventListener(CONSENT_EVENT, handler);
    window.removeEventListener("storage", storageHandler);
  };
}

/**
 * Production hostname allowlist for Meta Pixel eligibility.
 * ONLY these exact hostnames are considered production traffic:
 *   - truwe.in
 *   - www.truwe.in
 * Every other host (admin subdomain, lovable preview, localhost, IPs,
 * unknown custom hosts) is treated as internal/blocked.
 *
 * Pure predicate — no window/globals — for easy unit testing.
 */
export function isProductionHost(hostname: string | null | undefined): boolean {
  if (!hostname) return false;
  const h = hostname.toLowerCase().trim();
  return h === "truwe.in" || h === "www.truwe.in";
}

/**
 * Internal / non-production traffic exclusion. Meta events never fire unless
 * hostname is on the production allowlist AND the request is not from an
 * automated browser or admin opt-out.
 */
export function isInternalTraffic(): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (!isProductionHost(window.location.hostname)) return true;
    if ((navigator as Navigator & { webdriver?: boolean }).webdriver) return true;
    if (window.localStorage.getItem("truwe_internal") === "1") return true;
  } catch {
    /* ignore */
  }
  return false;
}

